import React, { useEffect, useRef, useState, useCallback } from "react";
import reciteMeButton from "@/assets/reciteme_button.png"; // ✅ adjust path if needed
import { DashboardService } from "@/shared/api/services/dashboardService";
import { ActionType } from "@/shared/api/types";

interface Position {
  top: number;
  left: number;
}

const BUTTON_SIZE = 45;
const BUTTON_MARGIN = 15;

const DRAG_THRESHOLD = 5; // Pixels of movement to consider it a drag vs click

const ReciteMeButton: React.FC = () => {
  const [position, setPosition] = useState<Position | null>(null);
  const [dragOffset, setDragOffset] = useState<Position | null>(null);
  const [hasBeenDragged, setHasBeenDragged] = useState(false);
  const buttonRef = useRef<HTMLDivElement | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const wasDraggedRef = useRef(false);

  const activateReciteMe = (): void => {
    DashboardService.logButtonAction({
      actionType: ActionType.BUTTON_CLICK,
      actionName: "ReciteMe Enable",
      pageName: "Global",
      subSection: "Accessibility",
    }).catch(() => {});
    window.loadService?.();
  };

  const handleClick = (): void => {
    // Only trigger click if it wasn't a drag gesture
    if (!wasDraggedRef.current) {
      activateReciteMe();
    }
  };

  // Keyboard activation so keyboard-only users (no mouse) can turn on Recite Me.
  // Enter/Space mirror the click behaviour expected of a button.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === "Enter" || event.key === " " || event.key === "Spacebar") {
      event.preventDefault();
      activateReciteMe();
    }
  };

  // Listen for ReciteMe close event from public/reciteme.js
  useEffect(() => {
    const handleReciteMeClose = () => {
      DashboardService.logButtonAction({
        actionType: ActionType.BUTTON_CLICK,
        actionName: "ReciteMe Close",
        pageName: "Global",
        subSection: "Accessibility",
      }).catch(() => {});
    };

    window.addEventListener("reciteMeClose", handleReciteMeClose);
    return () => window.removeEventListener("reciteMeClose", handleReciteMeClose);
  }, []);

  // Calculate default bottom-right position
  const getDefaultPosition = useCallback((): Position => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isNarrowViewport = viewportWidth <= 1000;
    const bottomOffset = isNarrowViewport ? 90 : BUTTON_MARGIN;

    return {
      left: viewportWidth - BUTTON_SIZE - BUTTON_MARGIN,
      top: viewportHeight - BUTTON_SIZE - bottomOffset,
    };
  }, []);

  // Clamp position to keep button within viewport
  const clampPosition = useCallback((pos: Position): Position => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const safetyMargin = 8;

    return {
      left: Math.max(
        safetyMargin,
        Math.min(viewportWidth - BUTTON_SIZE - safetyMargin, pos.left)
      ),
      top: Math.max(
        safetyMargin,
        Math.min(viewportHeight - BUTTON_SIZE - safetyMargin, pos.top)
      ),
    };
  }, []);

  // Initialize position on mount
  useEffect(() => {
    setPosition(getDefaultPosition());
  }, [getDefaultPosition]);

  // Handle window resize
  useEffect(() => {
    const handleResize = (): void => {
      setPosition((prevPosition) => {
        if (!prevPosition) {
          return getDefaultPosition();
        }

        // If button hasn't been manually dragged, reset to default corner position
        if (!hasBeenDragged) {
          return getDefaultPosition();
        }

        // If button was dragged, clamp it to stay within the new viewport bounds
        return clampPosition(prevPosition);
      });
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [hasBeenDragged, getDefaultPosition, clampPosition]);

  const handlePointerDown = (
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (!position || !buttonRef.current) {
      return;
    }

    // Prevent page scrolling / text selection while dragging on touch devices
    event.preventDefault();
    event.stopPropagation();

    buttonRef.current.setPointerCapture(event.pointerId);

    // Track starting position for drag detection
    pointerStartRef.current = { x: event.clientX, y: event.clientY };
    wasDraggedRef.current = false;

    setDragOffset({
      left: event.clientX - position.left,
      top: event.clientY - position.top,
    });
  };

  const handlePointerMove = (
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (!dragOffset || !pointerStartRef.current) {
      return;
    }

    // Check if movement exceeds drag threshold
    const deltaX = Math.abs(event.clientX - pointerStartRef.current.x);
    const deltaY = Math.abs(event.clientY - pointerStartRef.current.y);

    if (deltaX > DRAG_THRESHOLD || deltaY > DRAG_THRESHOLD) {
      wasDraggedRef.current = true;

      // Mark as dragged for resize behavior
      if (!hasBeenDragged) {
        setHasBeenDragged(true);
      }

      const nextLeft = event.clientX - dragOffset.left;
      const nextTop = event.clientY - dragOffset.top;

      // Clamp position to keep button inside viewport
      setPosition(clampPosition({ left: nextLeft, top: nextTop }));
    }
  };

  const handlePointerEnd = (
    event: React.PointerEvent<HTMLDivElement>,
  ): void => {
    if (buttonRef.current) {
      buttonRef.current.releasePointerCapture(event.pointerId);
    }
    setDragOffset(null);
    pointerStartRef.current = null;
  };

  const dynamicStyle: React.CSSProperties | undefined = position
    ? {
        position: "fixed",
        top: position.top,
        left: position.left,
        bottom: "auto",
        right: "auto",
      }
    : undefined;

  return (
    <>
      <style>
        {`
          .accessibility-icon__wrapper {
            bottom: 5.5rem !important;
          }

          .accessibilityIco {
            padding: 10px;
            z-index: 1000000;
            width: ${BUTTON_SIZE}px;
            height: ${BUTTON_SIZE}px;
            touch-action: none;
            -ms-touch-action: none;
            user-select: none;
            -webkit-user-drag: none;
            background-image: url(${reciteMeButton}) !important;
            background-size: cover;
            background-repeat: no-repeat;
            border: none;
            cursor: pointer;
          }

          @media (max-width: 1000px) {
            .accessibilityIco {
            }
          }

          .accessibilityIco:hover,
          .accessibilityIco:focus-visible {
            transform: scale(1.1);
          }

          /* Visible focus ring for keyboard users */
          .accessibilityIco:focus-visible {
            outline: 3px solid #00717A;
            outline-offset: 2px;
            border-radius: 4px;
          }
        `}
      </style>

      <div className="accessibility-icon__wrapper">
        <div
          ref={buttonRef}
          onClick={handleClick}
          onKeyDown={handleKeyDown}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerEnd}
          onPointerCancel={handlePointerEnd}
          role="button"
          tabIndex={0}
          aria-label="Turn on Recite Me accessibility toolbar"
          className="accessibilityIco"
          style={dynamicStyle}
        ></div>
      </div>
    </>
  );
};

export default ReciteMeButton;
